import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-brand-600 text-primary-foreground hover:bg-primary/90 hover:bg-brand-800 disabled:bg-gray-100 disabled:border-gray-200 border disabled:text-gray-400",
        white:
          "flex bg-white text-gray-800 border boder-gray-300 hover:bg-gray-200",
        red: "flex px-3 py-2 justify-center items-center self-stretch border rounded-lg bg-red-500 border-red-700 text-white font-semibold text-sm hover:bg-red-400",
        ghostRed:
          "flex px-3 py-2 justify-center items-center self-stretch border rounded-lg bg-white border-red-300 text-red-600 font-semibold text-sm hover:bg-red-100",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-gray-100 hover:text-accent-foreground",
        ghostWithBorders: "hover:bg-accent hover:text-accent-foreground border",
        link: "text-primary underline-offset-4 hover:underline",
        outlineBlue:
          "border-lg border border-blue-300 bg-transparent text-blue-600 font-semibold gap-1",
        blueLink: "text-brand-800 font-bold hover:text-brand-500",
        none: "hover:opacity-70",
        green:
          "flex px-3 py-2 justify-center items-center self-stretch border rounded-lg bg-green-600 border-green-700 text-white font-semibold text-sm hover:bg-green-400",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
